/**
 * Session assurance — pure helpers (2026-09-26, two-step verification).
 *
 * The backend puts the session's assurance in the JWT: `aal` (1|2), `amr` (factor types proven)
 * and `mfaAt` (unix seconds of the last second-factor proof). A token without those claims is aal1.
 * These helpers only READ the token for display and to decide whether to ask for a step-up first;
 * the server re-checks every protected action, so a wrong answer here costs a dialog, never access.
 */
import { jwtDecode } from "jwt-decode";
import type { JWTDecoded, SessionAssurance } from "../types";

/** Factor types (the non-factor link markers like "email_link" never count toward aal2). */
export const FACTOR_TYPES = ["pwd", "passkey", "email_code", "sms_code", "recovery"] as const;

/** Enable / disable / new recovery codes need a second-factor proof no older than this (backend rule). */
export const STEP_UP_FRESH_MINUTES = 10;
/** Secure pages default to this window (backend `requireTwoFactor({ maxAgeMinutes = 30 })`). */
export const SECURE_PAGE_FRESH_MINUTES = 30;
/** Ask again a little early so a proof never expires between the check and the server call. */
const SAFETY_MARGIN_SECONDS = 30;

export const AAL1: SessionAssurance = { aal: 1, amr: [] };

/** Decoded claims → assurance. Missing or malformed claims mean aal1. */
export function assuranceFromClaims(decoded: Partial<JWTDecoded> | null | undefined): SessionAssurance {
  if (!decoded) return AAL1;
  const amr = Array.isArray(decoded.amr) ? decoded.amr.filter((v): v is string => typeof v === "string") : [];
  const aal: 1 | 2 = decoded.aal === 2 ? 2 : 1;
  const mfaAt = typeof decoded.mfaAt === "number" && Number.isFinite(decoded.mfaAt) ? decoded.mfaAt : undefined;
  return mfaAt === undefined ? { aal, amr } : { aal, amr, mfaAt };
}

/** A raw token → assurance. An unreadable token is aal1 (the server decides what it really is). */
export function assuranceFromToken(token: string | null | undefined): SessionAssurance {
  if (!token) return AAL1;
  try {
    return assuranceFromClaims(jwtDecode<JWTDecoded>(token));
  } catch {
    return AAL1;
  }
}

/** The assurance of the token in localStorage right now (never stale after an adoptToken). */
export function readStoredAssurance(): SessionAssurance {
  let token: string | null = null;
  try { token = localStorage.getItem("auth_token"); } catch { /* storage blocked → aal1 */ }
  return assuranceFromToken(token);
}

/** True when the session is aal2 AND its last second-factor proof is within `maxAgeMinutes`. */
export function isFreshAal2(
  assurance: SessionAssurance | null | undefined,
  maxAgeMinutes: number = STEP_UP_FRESH_MINUTES,
  nowSeconds: number = Math.floor(Date.now() / 1000),
): boolean {
  if (!assurance || assurance.aal !== 2 || typeof assurance.mfaAt !== "number") return false;
  const age = nowSeconds - assurance.mfaAt;
  if (age < -60) return false; // a proof from the future = a broken clock claim; ask again
  return age <= maxAgeMinutes * 60 - SAFETY_MARGIN_SECONDS;
}

/** The GraphQL error codes the backend uses for step-up (UserFacingError extensions.code). */
export function errorCodes(err: any): string[] {
  const codes: string[] = [];
  if (err?.graphQLErrors?.length) codes.push(...err.graphQLErrors.map((e: any) => e?.extensions?.code));
  if (err?.extensions?.code) codes.push(err.extensions.code);
  if (err?.networkError?.result?.errors?.length) codes.push(...err.networkError.result.errors.map((e: any) => e?.extensions?.code));
  return codes.filter(Boolean);
}
export const isStepUpRequiredError = (err: any) => errorCodes(err).includes("STEP_UP_REQUIRED");
export const isTwoFactorSetupRequiredError = (err: any) => errorCodes(err).includes("TWO_FACTOR_SETUP_REQUIRED");
export const isStepUpFailedError = (err: any) => errorCodes(err).includes("STEP_UP_FAILED");

/** The server's own words for an error (UserFacingError messages are written for people). */
export function errorWords(err: any, fallback = "That didn't work. Please try again."): string {
  const gql = err?.graphQLErrors?.[0]?.message;
  if (gql) return gql;
  const msg: string | undefined = err?.message;
  if (msg && !/^Response not successful/i.test(msg)) return msg;
  return fallback;
}

// ─── What the Two-step verification card says (pure, so it is unit-tested) ─────────────

export interface TwoFactorStatusLike {
  enabled: boolean;
  enabledAt?: string | null;
  factorTypes: string[];
  canEnable: boolean;
  reason?: string | null;
  recoveryCodesRemaining: number;
}

export type TwoFactorView =
  | { state: "on"; headline: string; showTurnOn: false; showTurnOff: true; showNewCodes: true; codesLine: string; codesLow: boolean }
  | { state: "ready"; headline: string; showTurnOn: true; showTurnOff: false; showNewCodes: false; codesLine: null; codesLow: false }
  | { state: "not-ready"; headline: string; showTurnOn: false; showTurnOff: false; showNewCodes: false; codesLine: null; codesLow: false; why: string };

const formatDay = (value?: string | null): string | null => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
};

/**
 * Status → what the card shows. "Turn on" appears ONLY when the server says `canEnable`
 * (hide-don't-disable); the server re-checks when it is pressed.
 */
export function twoFactorView(status: TwoFactorStatusLike): TwoFactorView {
  if (status.enabled) {
    const since = formatDay(status.enabledAt);
    const n = Math.max(0, status.recoveryCodesRemaining | 0);
    return {
      state: "on",
      headline: since ? `On since ${since}` : "On",
      showTurnOn: false, showTurnOff: true, showNewCodes: true,
      codesLine: n === 1 ? "1 recovery code left" : `${n} recovery codes left`,
      codesLow: n <= 3,
    };
  }
  if (status.canEnable) {
    return { state: "ready", headline: "Off", showTurnOn: true, showTurnOff: false, showNewCodes: false, codesLine: null, codesLow: false };
  }
  return {
    state: "not-ready", headline: "Off", showTurnOn: false, showTurnOff: false, showNewCodes: false, codesLine: null, codesLow: false,
    why: status.reason || "Add a second way to sign in first — for example a passkey or a password — then you can turn this on.",
  };
}

/** Recovery codes as a downloadable text file's contents. */
export function recoveryCodesFileText(codes: string[], site: string, when: Date = new Date()): string {
  return [
    `Recovery codes for ${site}`,
    `Made ${when.toLocaleString()}`,
    "",
    "Each code works once. Keep them somewhere safe — they let you confirm it's you",
    "if you can't use your other sign-in methods.",
    "",
    ...codes,
    "",
  ].join("\n");
}
